import { TreeNode } from '~/models/shared/tree/types'
import { ClassifiedCategory } from '~/models/classified/category/types'
import { Schema } from '~/models/form/types'

export type CategoryMap = Map<string, ClassifiedCategory>

export interface Category extends TreeNode {
  humanName: string
  humanPath?: string
  classifiedsCount?: number
}

export interface HierarchicalCategory extends Category {
  ancestorIds: string[]
  selected: boolean
  childrenCanBeRetrieved: boolean
  unselectable: boolean
  original: boolean
}

export enum CategoryId {
  PLOT = 20002,
  PLOT_RENT = 20003,
  PLOT_SALE = 20004,
  PLOT_BOOKING = 20005,
  PLOT_WANTED_PROPERTY = 20100,
  JOBS = 30002,
  RENTALS = 40002,
  RENTALS_CARS = 41000,
  RENTALS_BIKES = 42000,
  RENTALS_BICYCLES = 47000,
  RENTALS_HOBBY = 49000,
  RENTALS_COMMERCIALS = 44000,
  RENTALS_TRUCKS = 46000,
  RENTALS_TAXIS = 44006,
  RENTALS_CAMPERS = 45000,
  RENTALS_BOATS = 43000,
  RENTALS_MISC = 48000,
  JOBS_WANTED = 30003,
  JOBS_OFFERED = 30004,
  XYMA = 50,
  XYMA_TOOLS_ACCESSORIES = 3909,
  XYMA_CATERING_EQUIPMENT = 4112,
  XYMA_HEALTH_BEAUTY_MEDICINE = 3726,
  XYMA_BUSINESS_EQUIPMENT = 2911,
  XYMA_ANTIQUES = 4723,
  XYMA_HOBBY_SPORTS = 2920,
  XYMA_TECHNOLOGY_SECURITY = 2915,
  XYMA_HOUSE_GARDEN = 2921,
  XYMA_FASHION = 2914,
  XYMA_CHILDREN_GOODS = 3581,
  XYMA_PETS = 2909,
  XYMA_SUPERMARKET = 3220,
  PARTS = 30,
  PARTS_CARS = 77,
  PARTS_TRACTORS = 1187,
  PARTS_BUILDERS = 1188,
  PARTS_TRUCKS = 394,
  PARTS_TOOLS = 7,
  PARTS_FORKLIFTS = 1189,
  PARTS_BUSES = 1190,
  PARTS_BIKES = 711,
  PARTS_BICYCLES = 3028,
  PARTS_BOATS = 34,
  PARTS_CAMPERS = 1042,
  VEHICLES = 15000,
  CARS = 15001,
  CARS_JEEP_SUV = 15218,
  CARS_VAN_MINI_BUS = 15864,
  CARS_PICKUP = 15214,
  CARS_SPORTS_CAR = 15685,
  CARS_COMMERCIAL = 15865,
  CARS_CABRIO = 15212,
  CARS_COUPE_SPORT = 15215,
  CARS_COMPACT_HATCHBACK = 15211,
  CARS_COMBI_CARAVAN = 15210,
  CARS_LIMO = 15213,
  CARS_TRAILER = 15207,
  CARS_OTHER = 15216,
  BIKES = 15002,
  BOATS = 15003,
  CATAMARAN = 15913,
  YATCH = 15368,
  JETSKI = 15370,
  BOAT_PARKINKG = 15765,
  BOAT_FISHING = 15377,
  SAILBOAT = 15375,
  SPEEDBOAT = 15365,
  BOAT_LICENSE = 15860,
  BOAT_FISHING_LICENSE = 15654,
  BOAT_OPEN = 15367,
  BOAT_SMALL = 15362,
  BOAT_FERRY = 15706,
  BOAT_TOURIST = 15707,
  BOAT_CABIN = 15764,
  BOAT_CANOE = 15824,
  BOAT_ENGINE = 15373,
  BOAT_TOW = 15828,
  BOAT_TRAILER = 15565,
  BOAT_TRECHANDIRI = 15810,
  BOAT_CARGO = 15758,
  BOAT_INFLATABLE = 15372,
  BOAT_OTHER = 15369,
  BICYCLES = 15004,
  BICYCLE = 17000,
  BICYCLE_ALL_MOUNTAIN = 15614,
  BICYCLE_BMX = 15615,
  BICYCLE_DIRT_JUMP = 15900,
  BICYCLE_DOWNHILL = 15843,
  BICYCLE_FAT = 15869,
  BICYCLE_TREKKING = 15874,
  BICYCLE_RACING = 15903,
  BICYCLE_ROAD = 15642,
  BICYCLE_CITY = 15643,
  BICYCLE_EXPANDABLE = 15640,
  BICYCLE_THREE_WHEEL = 15645,
  BICYCLE_OTHER = 15616,
  BICYCLE_ELECTRIC = 15646,
  BICYCLE_ELECTRIC_ALL_MOUNTAIN = 17008,
  BICYCLE_ELECTRIC_CARGO = 17014,
  BICYCLE_ELECTRIC_DOWNHILL = 17007,
  BICYCLE_ELECTRIC_FAT = 17009,
  BICYCLE_ELECTRIC_TREKKING = 17010,
  BICYCLE_ELECTRIC_ROAD = 17012,
  BICYCLE_ELECTRIC_CITY = 17011,
  BICYCLE_ELECTRIC_EXPANDABLE = 17013,
  BICYCLE_ELECTRIC_OTHER = 17015,
  BICYCLE_CHILDREN = 15641,
  BICYCLE_CHILDREN_ALL_MOUNTAIN = 17002,
  BICYCLE_CHILDREN_BMX = 17001,
  BICYCLE_CHILDREN_CITY = 17003,
  BICYCLE_CHILDREN_OTHER = 17004,
  SCOOTER = 17016,
  SCOOTER_ROLLERS = 17006,
  SCOOTER_SKATEBOARD = 15657,
  SCOOTER_ELECTRIC = 15891,
  SCOOTER_KIDS = 17005,
  SCOOTER_SIMPLE = 15656,
  CAMPERS = 15102,
  COMMERCIAL = 15101,
  TRUCKS = 15006,
  TRAILERS = 15013,
  TAXIS = 15014,
  CARAVANS = 15015,
  MOTORHOMES = 15016,
  RADIOCONTROLS = 15017,
  GOKARTS = 15018,
  WATERSPORTS = 15019,
  SNOWSPORTS = 15020,
  AIRSPORTS = 15021,
  VANS = 15005,
  BUSES = 15007,
  BUILDERS = 15008,
  SEMITRUCKS = 15009,
  SEMITRAILERS = 15010,
  TRACTORS = 15011,
  FORKLIFTS = 15012,
  CLASSIFIEDS = 20001,
  HOBBY = 15100,
  CARAVANHOUSE = 15430,
  CONTAINER = 15658,
  WANTED_PARTS = 50002,
  WANTED_PARTS_BIKES = 52000,
  // TODO these are hacky, remove later if possible
  DEALS = 'deals',
  TRADES = 'trades',
  SERVICE = 'service'
}

export interface CategoryGroup {
  label: string
  rootCategory: number
  suggestions: CategoriesSuggestion[]
}

export interface CategoriesSuggestion {
  category: {
    id: string | number
    path: Array<CategoryPath>
    humanName: string
    hasChildren: boolean
  }
  id: string
  index: string
  path: string
  rootCategory: number
  score: number
  targetUrl: string
  class: string
  selectable: boolean
}

export interface CategoryPath {
  humanName: string
  id: number
  name: string
}

export interface TreeLevelCategory {
  humanPath: string
  pluralName: string
  name: string
  hasChildren: boolean
  parentId?: number
  humanNamePlural: string
  humanName: string
  id: number
  movedToCategoryUrl: string | null
  movedToCategoryId: number | null
  isActive: boolean
  disabled?: boolean
  isRecent?: boolean
}

export type TreeLevelNode = Pick<
  TreeLevelCategory,
  | 'id'
  | 'humanName'
  | 'parentId'
  | 'hasChildren'
  | 'disabled'
  | 'isActive'
  | 'humanPath'
>

export interface TreeLevelCategoryResult {
  levels: TreeLevelCategory[][]
  hasRecentCategories: boolean
}

export interface CategoryMapEntry {
  selectedCategory?: TreeLevelCategory
  editCollapse: boolean
}

export interface TreeLevelGetterOptions {
  rootUrlParam: boolean
}

export interface CmtAction {
  humanName: string
  actionDate: null | string
  actionType: string
  actionUser: null | string
  blockedAction: null | boolean
  created: string
  id: number
  nextValue: Record<string, any>
  previousValue: Record<string, any>
  relatedInfo: {
    backendClass: string
    id: number
  }
  backendClass: string
  scheduleInfo: Record<string, any>
  scheduled: boolean
  status: CmtActionStatus
  user: {
    displayName: string
    id: number
  }
}

export interface ContentManagementToolsCategoryResponse {
  category: Category
  classifiedsCount: number
  values: {
    name: string
    translations: { [key: string]: string }
  }
  schema: Schema
  pendingActions: CmtAction[]
}

export interface TranslationValues {
  translations: { [key: string]: string }
}

export interface ContentManagementToolsFieldResponse {
  category: Category
  field: any
  values: ContentManagementToolsFieldValues
  schema: Schema
}

export interface ContentManagementToolsExtraResponse {
  extra: ContentManagementToolsExtraValues
  categories: Category[]
  values: {
    translations: { [key: string]: string }
  }
  schema: Schema
}

export interface ContentManagementToolsExtraValues {
  group: string
  humanName: string
  id: number
  name: string
  value: number
  translations: { [key: string]: string }
}

export interface ContentManagementToolsFieldValues {
  name?: string
  isMultiple: boolean
  isFacetable: boolean
  editable?: boolean
  fieldType?: string
  fieldsetId?: number | null
  fieldsetOrder?: number
  formOrder: { [key: string]: string }[]
  facetOrder: { [key: string]: string }[]
  excludeForCategories: []
  translations: { [key: string]: string }
  lookupValues?: {
    lookupKey: string
    translations: { [key: string]: string }
  }[]
  min?: number
  max?: number
  step?: number
  valueType?: string
  valueMetric?: string
}

export interface CategoryField {
  humanName: string
  categoryId: number
  defaults: {
    lookupGroup: string
    form: {
      changeable: boolean
    }
  }
  active: boolean
  editable: boolean
  fieldSetId: number
  fieldType: string
  fieldsetOrder: null
  id: number
  info: string
  name: string
  version: number
}

export type CmtActionStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'failed'
  | 'completed'

export interface QuickSearchCategory {
  name: string
  nameTranslationKey: string
  id: CategoryId
  link?: {
    name?: string
    params?: Record<string, string>
    path?: string
  }
  children?: QuickSearchCategory[]
  facets?: Array<{ name: string }>
  isNew?: boolean
}
export interface QuickSearchCategories {
  [key: string]: QuickSearchCategory
}
